import {
  Box,
  Checkbox,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  Link,
  Button,
  IconButton,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import HelpIcon from '@mui/icons-material/HelpOutlineRounded'

import { TextTag } from 'src/components/text-tag'
import { getColorOrNameByConsumerScanStatus } from 'src/common/utils/get-color-or name-by-scan-status'
import { useMe, useTableItemsCheck } from 'src/hooks'
import {
  capitalizeFirstLetter,
  getScanExplorerTxUrl,
  getTableTimestamp,
  shortenStringMiddle2,
  truncateString,
} from 'src/common/utils'
import { ERewardPointsTypes, EScanNetwork, IRewardPointsBaseWithId } from 'src/types'

import { LinkIcon } from 'src/assets/icons'
import { TrashIcon } from 'src/assets/icons/v2/TrashIcon'

import { useStyles } from './../styles'
import { ERewardActivityStatus, IRewardActivityBaseWithId } from 'src/types/rewadr-acrivity.type'
import { IconWithTooltip } from 'src/components/icon-with-tooltip'
import { height } from '@mui/system'

interface IChangesGridItemProps
  extends Pick<ReturnType<typeof useTableItemsCheck>, 'isItemSelected' | 'handleItemCheck'> {
  item: IRewardActivityBaseWithId & {
    referralUser?: { id: string; email: string; name: string } | null
  }
  handleDeclineInfoModalOpen: (declineReason: string) => void
  handleFeedbackModalOpen: (itemIds: string[]) => void
  rewardPointsInfo: void | IRewardPointsBaseWithId[] | undefined
  handleItemRemove: (itemId: string) => Promise<void>
}

export const ConsumerScanListItem = ({
  item,
  isItemSelected,
  handleItemCheck,
  handleDeclineInfoModalOpen,
  handleFeedbackModalOpen,
  rewardPointsInfo,
  handleItemRemove,
}: IChangesGridItemProps) => {
  // const [itemStatus, setItemStatus] = useState<EScanStatus>(item.status)

  const classes = useStyles()
  const theme = useTheme()

  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))
  const isMobile = useMediaQuery(theme.breakpoints.down('laptop'))

  const { data: user } = useMe()

  let rewardPointInfo: IRewardPointsBaseWithId | undefined

  if (rewardPointsInfo) {
    rewardPointInfo = rewardPointsInfo.find((rewardPoint) => rewardPoint.type === item.type)
  }

  return (
    <>
      <TableRow>
        {!isMobile && (
          <TableCell sx={{ paddingX: '0px' }} align="center">
            <Checkbox
              disabled={
                item.status !== ERewardActivityStatus.PENDING ||
                item.type === ERewardPointsTypes.REFERRAL_PROGRAM
              }
              checked={isItemSelected(item.id)}
              onChange={handleItemCheck(item.id)}
              sx={{ color: theme.palette.custom.grey2 }}
            />
          </TableCell>
        )}
        <TableCell sx={{ pl: '8px', pr: '0px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: theme.palette.custom.grey2,
                }}
              >
                 {item.type === ERewardPointsTypes.SCAN_TO_EARN
                  ? 'Scan-to-Earn'
                  : item.type === ERewardPointsTypes.REFERRAL_PROGRAM
                    ? 'Referral Program'
                    : rewardPointInfo?.name}
              </Typography>
              {!!rewardPointInfo && <IconWithTooltip tooltipText={rewardPointInfo.description} />}
            </Box>
            {item?.referralUser && item.type === ERewardPointsTypes.REFERRAL_PROGRAM && (
              <Typography
                sx={{
                  fontSize: '14px',
                }}
              >
                {isTablet
                  ? truncateString(item.referralUser?.name || item.referralUser?.email)
                  : item.referralUser?.name || item.referralUser?.email}
              </Typography>
            )}
            {item?.consumerScan && (
              <Typography
                sx={{
                  fontSize: '12px',
                }}
              >
                {isTablet
                  ? truncateString(item?.consumerScan?.company_name)
                  : item?.consumerScan?.company_name}
              </Typography>
            )}
            {item?.consumerScan && (
              <Typography
                sx={{
                  fontSize: '14px',
                }}
              >
                {isTablet
                  ? truncateString(item?.consumerScan?.product_name)
                  : item?.consumerScan?.product_name}
              </Typography>
            )}
            {item?.consumerScan && (
              <Typography
                sx={{
                  fontSize: '12px',
                  color: theme.palette.custom.grey2,
                }}
              >
                {item?.consumerScan?.barcode_id}
              </Typography>
            )}
            {item?.consumerScan && (
              <Typography
                sx={{
                  fontSize: '12px',
                }}
              >
                {capitalizeFirstLetter(item?.consumerScan?.product_type)}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {item?.consumerScan?.user_tx && (
                <Link
                  sx={{
                    display: 'flex',
                    gap: '5px',
                    alignItems: 'center',
                    fontSize: '12px',
                    wordBreak: 'break-all',
                  }}
                  component={RouterLink}
                  to={getScanExplorerTxUrl(item?.consumerScan.user_tx, EScanNetwork.BASE)}
                  target="_blank"
                >
                  {shortenStringMiddle2(item?.consumerScan.user_tx, isTablet ? 11 : 25, '...')}
                  <LinkIcon className={classes.linkIcon} />
                </Link>
              )}
              {isMobile &&
                item.status === ERewardActivityStatus.PENDING &&
                item.type === ERewardPointsTypes.SCAN_TO_EARN && (
                  <IconButton
                    // disabled={isRemovingLoading}
                    sx={{
                      padding: '3px',
                    }}
                    onClick={() => {
                      handleItemRemove(item.id)
                    }}
                  >
                    <TrashIcon size={16} color={theme.palette.custom.warning2} />
                  </IconButton>
                )}
            </Box>
            {item?.payload?.challenges && item.payload.challenges.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  flexWrap: 'wrap',
                  maxWidth: '100%',
                  pt: '12px',
                }}
              >
                {item.payload.challenges.map((item) => (
                  <TextTag
                    label={item.name}
                    sx={{ wordBreak: 'break-all', maxWidth: isTablet ? '140px' : '100%' }}
                    bgColor={theme.palette.custom.grey}
                    color={theme.palette.custom.darkBlue2}
                  />
                ))}
              </Box>
            )}

            {item.type === ERewardPointsTypes.TRAI_RECEIVED &&
              item?.payload?.trai_challenge_info && (
                <>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      // color: theme.palette.custom.grey2,
                    }}
                  >
                    $TRAI: {item.payload.trai_challenge_info?.trai ?? 0}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: theme.palette.custom.grey2,
                    }}
                  >
                    Points: {item.payload.trai_challenge_info?.points ?? 0}
                  </Typography>
                  {item.payload.trai_challenge_info?.challenge?.name && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        flexWrap: 'wrap',
                        maxWidth: '100%',
                        pt: '6px',
                      }}
                    >
                      <TextTag
                        label={item.payload.trai_challenge_info.challenge.name}
                        sx={{ wordBreak: 'break-all', maxWidth: isTablet ? '140px' : '100%' }}
                        bgColor={theme.palette.custom.grey}
                        color={theme.palette.custom.darkBlue2}
                      />
                    </Box>
                  )}
                </>
              )}
          </Box>
        </TableCell>

        {!isMobile && (
          <TableCell sx={{ paddingX: '8px' }} align="center">
            {item.type !== ERewardPointsTypes.TRAI_RECEIVED ? item.amount : '-'}
          </TableCell>
        )}
        {/* <TableCell align="center">{item.product_type}</TableCell> */}

        <TableCell align="center" sx={{ paddingX: '8px', height: '100%', verticalAlign: 'middle' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: '100%',
              gap: '10px',
            }}
          >
            <Typography fontSize={'16px'} fontWeight={500} sx={{ minHeight: '24px' }}>
              {isMobile ? (item.type !== ERewardPointsTypes.TRAI_RECEIVED ? item.amount : '') : ''}
            </Typography>
            {item.status === ERewardActivityStatus.DECLINED ? (
              <Button
                disabled={!item?.payload?.decline_reason}
                onClick={() => {
                  handleDeclineInfoModalOpen(item?.payload?.decline_reason || '')
                }}
                sx={{ textTransform: 'none', p: '4px' }}
              >
                <TextTag
                  label={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '3px',
                      }}
                    >
                      {getColorOrNameByConsumerScanStatus('text', item.status)}
                      {item?.payload?.approval_feedbacks &&
                        item.payload.approval_feedbacks.length > 0 && (
                          <HelpIcon
                            sx={{
                              color: theme.palette.common.white,
                              width: '15px',
                              height: '15px',
                            }}
                          />
                        )}
                    </Box>
                  }
                  bgColor={getColorOrNameByConsumerScanStatus('color', item.status)}
                />
              </Button>
            ) : (
              <Button
                disabled={
                  !(item?.payload?.approval_feedbacks && item.payload.approval_feedbacks.length > 0)
                }
                onClick={() => {
                  handleFeedbackModalOpen(item?.payload?.approval_feedbacks || [])
                }}
                sx={{ textTransform: 'none', p: '4px' }}
              >
                <TextTag
                  label={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '3px',
                      }}
                    >
                      {getColorOrNameByConsumerScanStatus('text', item.status)}
                      {item?.payload?.approval_feedbacks &&
                        item.payload.approval_feedbacks.length > 0 && (
                          <HelpIcon
                            sx={{
                              color: theme.palette.common.white,
                              width: '15px',
                              height: '15px',
                            }}
                          />
                        )}
                    </Box>
                  }
                  bgColor={getColorOrNameByConsumerScanStatus('color', item.status)}
                />
              </Button>
            )}
            <Typography
              sx={{
                width: '100%',
                fontSize: '11px',
                color: theme.palette.custom.grey2,
              }}
            >
              {getTableTimestamp({ date: item?.created_at, timezone: user?.timezone })}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
    </>
  )
}
